import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import styled, { css } from "styled-components";

import { DESKTOP_BREAKPOINT } from "../../components/AppShell/styles";
import { Skeleton } from "../../components/Skeleton";
import { useAccountCurrency } from "../../hooks/AccountCurrency";
import PlusIcon from "../../icons/Plus";
import {
  AgendaBooking,
  AgendaBookingStatus,
  confirmBooking,
  declineBooking,
  errorCodeOf,
  listBookings,
} from "../../services/agenda";
import { SmallButton } from "../Domains/styles";
import { NewSiteCard } from "../Sites/styles";
import { BookingModal, BookingStep, paidOf, paymentLine, refundOf, STATUS_COLORS } from "./BookingModal";
import { formatCents } from "./format";
import { NewBookingModal } from "./NewBookingModal";
import { ListStack, Note } from "./styles";
import { TimezoneNotice, todayIn, useAgendaTimezone } from "./timezone";

const prefix = "pages.agenda.bookings";

// phones: five days, so each one has room (the desktop is the AppShell's, with the sidebar)
const DESKTOP = `(min-width: ${DESKTOP_BREAKPOINT})`;
const desktopNow = () => window.matchMedia?.(DESKTOP).matches ?? true;
const useDayCount = () => {
  const [desktop, setDesktop] = useState(desktopNow);
  useEffect(() => {
    const media = window.matchMedia?.(DESKTOP);
    if (!media) return;
    const update = () => setDesktop(media.matches);
    media.addEventListener("change", update);
    return () => media.removeEventListener("change", update);
  }, []);
  return desktop ? 7 : 5;
};

// a day as a date at noon UTC: added to and formatted in UTC, it never slips to the day before
const noon = (day: string) => new Date(`${day}T12:00:00Z`);
const addDays = (day: string, days: number) => {
  const date = noon(day);
  date.setUTCDate(date.getUTCDate() + days);
  return date.toISOString().slice(0, 10);
};

// a day that exists: February 31st rolls over to March 3rd, so it doesn't come back as it went in
const isDay = (value: string | null): value is string =>
  !!value && /^\d{4}-\d{2}-\d{2}$/.test(value) && !Number.isNaN(noon(value).getTime()) && addDays(value, 0) === value;

// a booking awaiting its payment holds its time too (for a few minutes): it counts in the day, though it isn't confirmed
const holdsTime = (booking: AgendaBooking) =>
  booking.status === "pending" || booking.status === "confirmed" || booking.status === "awaiting_payment";

// the owner has to act on it (answer a request, or reschedule/refund a payment that lost its time): the week marks it
const needsOwner = (booking: AgendaBooking) => booking.status === "pending" || booking.status === "paid_no_slot";

type Props = { siteId: string; isPlus: boolean };

/**
 * The bookings: the week's days on top, the chosen day's bookings (one frame each, as /domains) with what's
 * confirmed in it. A request waiting for the owner is confirmed or declined right in its frame.
 */
export const Bookings = ({ siteId, isPlus }: Props) => {
  const { t, i18n } = useTranslation();
  const { currency, loaded: currencyLoaded } = useAccountCurrency();
  const [params, setParams] = useSearchParams();
  // "today" is the agenda's, even on a computer in another timezone
  const timezone = useAgendaTimezone(siteId);
  const day = isDay(params.get("dia")) ? (params.get("dia") as string) : todayIn(timezone);
  const dayCount = useDayCount();
  const [start, setStart] = useState(day);
  const [bookings, setBookings] = useState<AgendaBooking[]>();
  // the day waits for the account's currency too: the browser's guess would show the prices in another one first
  const listed = currencyLoaded ? bookings : undefined;
  const [hasError, setError] = useState(false);
  const [open, setOpen] = useState<AgendaBooking | null>(null);
  // the modal can open on a question: refund a paid booking, or refund it now that it's declined
  const [openStep, setOpenStep] = useState<BookingStep>("detail");
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<AgendaBooking | null>(null);
  // each request in its frame answers on its own: confirming (true) or declining, and what went wrong
  const [answering, setAnswering] = useState<Record<string, { confirming: boolean }>>({});
  const [answerErrors, setAnswerErrors] = useState<Record<string, true>>({});

  // each load counts: only the last one asked (the week on screen) is shown, whatever answers last
  const lastLoad = useRef(0);
  const load = useCallback(() => {
    const id = ++lastLoad.current;
    setError(false);
    listBookings(siteId, start, addDays(start, dayCount - 1))
      .then(list => id === lastLoad.current && setBookings(list.items))
      .catch(() => id === lastLoad.current && setError(true));
  }, [siteId, start, dayCount]);

  // another week: its own bookings (loading meanwhile), never the last week's
  useEffect(() => {
    setBookings(undefined);
    load();
  }, [load]);

  // a day out of the week shown (a booking made far ahead, the browser's back): its week instead
  useEffect(() => {
    if (day < start || day > addDays(start, dayCount - 1)) setStart(day);
  }, [day, start, dayCount]);

  const choose = (next: string) =>
    setParams(
      current => {
        const query = new URLSearchParams(current);
        query.set("dia", next);
        return query;
      },
      { replace: true }
    );

  const moveWeek = (weeks: number) => {
    const next = addDays(start, weeks * dayCount);
    setStart(next);
    choose(next);
  };

  const days = Array.from({ length: dayCount }, (_, index) => addDays(start, index));
  const ofDay = (listed ?? []).filter(booking => booking.date === day);
  // what's confirmed: the services' prices (not what was paid, which can carry the fees), as always
  const confirmedCents = ofDay
    .filter(booking => booking.status === "confirmed")
    .reduce((sum, booking) => sum + (booking.service.priceCents ?? 0), 0);
  const format = (date: string, options: Intl.DateTimeFormatOptions) =>
    new Intl.DateTimeFormat(i18n.language, { ...options, timeZone: "UTC" }).format(noon(date));
  // i18next 20 reads 0 as singular in Portuguese ("0 agendamento"): none has its own text
  const countText = (count: number) => (count ? t(`${prefix}.count`, { count }) : t(`${prefix}.countNone`));

  const show = (booking: AgendaBooking, step: BookingStep = "detail") => {
    setOpenStep(step);
    setOpen(booking);
  };

  const changed = (booking: AgendaBooking, reload = false) => {
    setBookings(shown => shown?.map(item => (item.id === booking.id ? booking : item)));
    setOpen(null);
    // a confirmed request to move another booking cancels that one, and refunding the old booking of a
    // reschedule cancels the new one too: the list again
    if (reload || booking.replacesId) load();
  };

  // Confirm or decline a request from its frame
  const answer = async (booking: AgendaBooking, change: typeof confirmBooking) => {
    const without = <T,>(record: Record<string, T>) =>
      Object.fromEntries(Object.entries(record).filter(([id]) => id !== booking.id));
    setAnswering(current => ({ ...current, [booking.id]: { confirming: change === confirmBooking } }));
    setAnswerErrors(without);
    try {
      const result = await change(siteId, booking.id);
      changed(result);
      // declining doesn't give the money back: the owner is asked
      if (change === declineBooking && paidOf(booking)) show(result, "offerRefund");
    } catch (error) {
      setAnswerErrors(current => ({ ...current, [booking.id]: true }));
      // answered already (by the link in the WhatsApp or the e-mail): what's on screen is old
      if (errorCodeOf(error) === "NOT_ALLOWED") load();
    } finally {
      setAnswering(without);
    }
  };

  return (
    <Wrapper>
      <TimezoneNotice timezone={timezone} />
      <Head>
        <h2>{format(day, { weekday: "long", day: "numeric", month: "long" })}</h2>
        {/* the day's count and what's confirmed, on the date's right (its place pulsing while it loads) */}
        {listed ? (
          <p className="summary">
            <span>{countText(ofDay.filter(holdsTime).length)}</span>
            {confirmedCents > 0 && (
              <span>{t(`${prefix}.total`, { total: formatCents(confirmedCents, currency) })}</span>
            )}
          </p>
        ) : (
          <Skeleton className="summary-loading" aria-hidden="true" />
        )}
      </Head>

      <Week>
        <button type="button" className="arrow" aria-label={t(`${prefix}.previous`)} onClick={() => moveWeek(-1)}>
          ‹
        </button>
        <div className="days">
          {days.map(date => {
            // a paid booking with no time holds none, but is on its day to be dealt with
            const count = (bookings ?? []).filter(
              booking => booking.date === date && (holdsTime(booking) || booking.status === "paid_no_slot")
            ).length;
            const waiting = (bookings ?? []).some(booking => booking.date === date && needsOwner(booking));
            return (
              <DayChip
                key={date}
                type="button"
                $selected={date === day}
                $today={date === todayIn(timezone)}
                aria-pressed={date === day}
                onClick={() => choose(date)}>
                <small>{format(date, { weekday: "short" }).replace(".", "")}</small>
                <strong className="date">{`${date.slice(8, 10)}/${date.slice(5, 7)}`}</strong>
                {/* phones: only the day's number (the month is in the title above); the date above is still read */}
                <strong className="number" aria-hidden="true">
                  {date.slice(8, 10)}
                </strong>
                {/* the day's bookings in a badge on its corner: nothing in the day moves */}
                {count > 0 && (
                  <span className={waiting ? "badge waiting" : "badge"} aria-label={t(`${prefix}.count`, { count })}>
                    {count}
                  </span>
                )}
              </DayChip>
            );
          })}
        </div>
        <button type="button" className="arrow" aria-label={t(`${prefix}.next`)} onClick={() => moveWeek(1)}>
          ›
        </button>
      </Week>

      <ListStack>
        {/* Before the list, as "Create Keepo" on /sites */}
        {isPlus && (
          <NewSiteCard type="button" onClick={() => setCreating(true)}>
            <span className="icon">
              <PlusIcon width={14} height={14} color="currentColor" />
            </span>
            {t(`${prefix}.new.title`)}
          </NewSiteCard>
        )}

        {hasError && (
          <Note role="alert">
            {t(`${prefix}.loadError`)}{" "}
            <button type="button" onClick={load}>
              {t(`${prefix}.retry`)}
            </button>
          </Note>
        )}
        {!listed && !hasError && (
          <SkeletonList aria-busy="true" aria-label={t(`${prefix}.loading`)}>
            {[0, 1, 2].map(row => (
              <li key={row}>
                <Skeleton className="pill" />
                <span className="lines">
                  <Skeleton className="name" />
                  <Skeleton className="meta" />
                </span>
              </li>
            ))}
          </SkeletonList>
        )}
        {listed && ofDay.length === 0 && <Empty>{t(`${prefix}.empty`)}</Empty>}
        {ofDay.length > 0 && (
          // one frame per booking: its start in a small tag before the name, the end with the service
          <DayList aria-label={t(`${prefix}.dayList`)}>
            {ofDay.map(booking => (
              <li
                key={booking.id}
                className={
                  booking.status === "paid_no_slot"
                    ? "highlight"
                    : // not yet confirmed or paid: nothing to do, so it is faded though its time is held
                      holdsTime(booking) && booking.status !== "awaiting_payment"
                      ? undefined
                      : "faded"
                }>
                <div
                  className="card"
                  role="button"
                  tabIndex={0}
                  aria-label={`${booking.time} ${booking.customer.name} ${t(`${prefix}.statuses.${booking.status}`)}`}
                  onClick={() => show(booking)}
                  onKeyDown={event => {
                    if (event.key === "Enter" || event.key === " ") {
                      event.preventDefault();
                      show(booking);
                    }
                  }}>
                  <span className="pill">{booking.time}</span>
                  <span className="info">
                    <span className="row">
                      <h4>{booking.customer.name}</h4>
                      <Status $status={booking.status}>{t(`${prefix}.statuses.${booking.status}`)}</Status>
                    </span>
                    <p>
                      {[
                        booking.service.name,
                        formatCents(booking.service.priceCents, currency),
                        t(`${prefix}.until`, { time: booking.endTime }),
                        booking.replacesId && t(`${prefix}.moveRequest`),
                      ]
                        .filter(Boolean)
                        .join(" · ")}
                    </p>
                    {paymentLine(booking, t) && <p>{paymentLine(booking, t)}</p>}
                    {answerErrors[booking.id] && <p className="error">{t(`${prefix}.changeError`)}</p>}
                  </span>
                  {/* whatever the plan: the customers wait for an answer */}
                  {booking.status === "pending" && (
                    <span
                      className="actions"
                      onClick={event => event.stopPropagation()}
                      onKeyDown={event => event.stopPropagation()}>
                      <SmallButton
                        type="button"
                        disabled={!!answering[booking.id]}
                        $loading={answering[booking.id]?.confirming === false}
                        onClick={() => answer(booking, declineBooking)}>
                        {t(`${prefix}.decline`)}
                      </SmallButton>
                      <SmallButton
                        type="button"
                        $primary
                        disabled={!!answering[booking.id]}
                        $loading={answering[booking.id]?.confirming === true}
                        onClick={() => answer(booking, confirmBooking)}>
                        {t(`${prefix}.confirm`)}
                      </SmallButton>
                    </span>
                  )}
                  {/* paid, but its time was lost: another time, or the money back */}
                  {booking.status === "paid_no_slot" && (
                    <span
                      className="actions"
                      onClick={event => event.stopPropagation()}
                      onKeyDown={event => event.stopPropagation()}>
                      {refundOf(booking) && (
                        <SmallButton
                          type="button"
                          disabled={refundOf(booking) === "owner-only"}
                          title={refundOf(booking) === "owner-only" ? t(`${prefix}.refundOwnerOnly`) : undefined}
                          onClick={() => show(booking, "refund")}>
                          {t(`${prefix}.refund`)}
                        </SmallButton>
                      )}
                      {/* rescheduling is editing: PLUS */}
                      {isPlus && (
                        <SmallButton type="button" $primary onClick={() => setEditing(booking)}>
                          {t(`${prefix}.reschedulePaid`)}
                        </SmallButton>
                      )}
                    </span>
                  )}
                </div>
              </li>
            ))}
          </DayList>
        )}
      </ListStack>

      {open && (
        <BookingModal
          // another booking or another question: a new modal, never the last one's state
          key={open.id + openStep}
          siteId={siteId}
          booking={open}
          step={openStep}
          isPlus={isPlus}
          onChanged={changed}
          onOutdated={() => {
            setOpen(null);
            load();
          }}
          onEdit={() => {
            setEditing(open);
            setOpen(null);
          }}
          onExit={() => setOpen(null)}
        />
      )}
      {editing && (
        <NewBookingModal
          siteId={siteId}
          date={day}
          booking={editing}
          onExit={() => setEditing(null)}
          onCreated={booking => {
            setEditing(null);
            choose(booking.date);
            load();
          }}
        />
      )}
      {creating && (
        <NewBookingModal
          siteId={siteId}
          date={day}
          onExit={() => setCreating(false)}
          onCreated={booking => {
            setCreating(false);
            choose(booking.date);
            load();
          }}
        />
      )}
    </Wrapper>
  );
};

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  font-family: Inter, sans-serif;
`;

const Head = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  align-items: center;
  justify-content: space-between;

  h2 {
    font-size: 1.125rem;
    font-weight: 700;
    color: var(--color-neutral-white);
  }

  h2::first-letter {
    text-transform: uppercase;
  }

  .summary {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    font-size: 0.8125rem;
    line-height: 1.125rem;
    color: var(--color-neutral-150);
    text-align: right;
  }

  .summary span:first-child {
    font-weight: 700;
    color: var(--color-neutral-white);
  }

  .summary-loading {
    width: 7rem;
    height: 0.875rem;
  }

  /* phones: the summary in one line under the date, lined up with it */
  @media (max-width: 1023px) {
    flex-direction: column;
    gap: 0.25rem;
    align-items: flex-start;

    .summary {
      flex-direction: row;
      flex-wrap: wrap;
      align-items: baseline;
      text-align: left;
    }

    .summary span + span::before {
      margin: 0 0.375rem;
      content: "·";
    }
  }
`;

const Week = styled.div`
  display: flex;
  /* room for the days' badges */
  padding-top: 0.375rem;
  gap: 0.5rem;
  align-items: center;

  .days {
    display: grid;
    flex: 1;
    /* as many equal columns as days shown */
    grid-auto-columns: minmax(0, 1fr);
    grid-auto-flow: column;
    gap: 0.375rem;
  }

  .arrow {
    flex: none;
    width: 2rem;
    height: 2rem;
    font-size: 1.25rem;
    color: var(--color-neutral-150);
    border-radius: 100px;
  }

  .arrow:hover {
    color: var(--color-neutral-white);
    background: var(--color-neutral-dark);
  }

  /* phones: smaller arrows, the room goes to the five days */
  @media (max-width: 1023px) {
    gap: 0.25rem;

    .arrow {
      width: 1.5rem;
    }
  }
`;

const DayChip = styled.button<{ $selected: boolean; $today: boolean }>`
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 0.125rem;
  align-items: center;
  padding: 0.5rem 0.25rem;
  color: var(--color-neutral-white);
  background: var(--color-neutral-dark);
  border: 1px solid ${({ $today }) => ($today ? "var(--color-neutral-150)" : "transparent")};
  border-radius: 0.875rem;
  transition: border-color 0.15s;

  &:hover {
    border-color: var(--color-neutral-base);
  }

  small {
    font-size: 0.6875rem;
    color: var(--color-neutral-150);
    text-transform: capitalize;
  }

  strong {
    font-size: 0.875rem;
    font-weight: 700;
  }

  .number {
    display: none;
  }

  .badge {
    position: absolute;
    top: -0.375rem;
    right: -0.375rem;
    display: grid;
    place-content: center;
    min-width: 1.125rem;
    height: 1.125rem;
    padding: 0 0.25rem;
    font-size: 0.625rem;
    font-weight: 700;
    line-height: 1;
    color: var(--color-neutral-darkness);
    background: var(--color-feedback-success);
    border-radius: 100px;
  }

  .badge.waiting {
    background: #ffb066;
  }

  /* phones: the day's number a bit bigger and the badge inside the strip */
  @media (max-width: 1023px) {
    min-width: 0;
    padding: 0.5rem 0;

    .number {
      display: block;
      font-size: 1rem;
    }

    /* out of sight, still read */
    .date {
      position: absolute;
      width: 1px;
      height: 1px;
      overflow: hidden;
      clip: rect(0 0 0 0);
      white-space: nowrap;
    }

    .badge {
      right: -0.25rem;
    }
  }

  ${({ $selected }) =>
    $selected &&
    css`
      color: var(--color-neutral-darkness);
      background: var(--color-neutral-white);
      border-color: var(--color-neutral-white);

      &:hover {
        border-color: var(--color-neutral-white);
      }

      small {
        color: var(--color-neutral-darkness);
      }
    `}
`;

const Empty = styled.p`
  padding: 2rem 1rem;
  font-size: 0.875rem;
  color: var(--color-neutral-150);
  text-align: center;
  background: var(--color-neutral-dark);
  border-radius: 1rem;
`;

/** A booking's status, as a domain's on /domains: a colored dot and its word. */
const Status = styled.span<{ $status: AgendaBookingStatus }>`
  display: inline-flex;
  gap: 0.375rem;
  align-items: center;
  margin-top: 0.25rem;
  font-size: 0.75rem;
  font-weight: 700;
  line-height: 1rem;
  color: ${({ $status }) => STATUS_COLORS[$status]};

  &::before {
    width: 6px;
    height: 6px;
    content: "";
    background-color: currentColor;
    border-radius: 100%;
  }
`;

const DayList = styled.ul`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  padding: 0;
  list-style: none;

  /* the start, in a small dark tag */
  .pill {
    flex: none;
    padding: 0.375rem 0.625rem;
    font-size: 0.875rem;
    font-weight: 700;
    line-height: 1.25rem;
    color: var(--color-neutral-white);
    font-variant-numeric: tabular-nums;
    background: var(--color-neutral-darkness);
    border-radius: 0.625rem;
  }

  .faded .pill {
    color: var(--color-neutral-150);
  }

  .card {
    display: flex;
    gap: 1rem;
    align-items: center;
    min-width: 0;
    padding: 0.75rem 1rem 0.75rem 0.75rem;
    text-align: left;
    cursor: pointer;
    background: var(--color-neutral-dark);
    border: 1px solid transparent;
    border-radius: 1rem;
    transition: border-color 0.15s;
  }

  .card:hover {
    border-color: var(--color-neutral-base);
  }

  /* paid with no time: the owner has to act */
  .highlight .card {
    border-color: var(--color-orange);
  }

  .faded h4 {
    color: var(--color-neutral-150);
  }

  .info {
    display: flex;
    flex: 1;
    flex-direction: column;
    min-width: 0;
  }

  .row {
    display: flex;
    gap: 0.5rem;
    align-items: center;
    min-width: 0;
  }

  .row > span {
    flex: none;
    margin-top: 0;
  }

  h4 {
    overflow: hidden;
    font-size: 0.9375rem;
    font-weight: 700;
    line-height: 1.25rem;
    color: var(--color-neutral-white);
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .info p {
    margin: 0;
    font-size: 0.8125rem;
    line-height: 1.125rem;
    color: var(--color-neutral-150);
  }

  .info .error {
    color: var(--color-feedback-danger);
  }

  .actions {
    display: flex;
    flex: none;
    gap: 0.5rem;
  }

  /* phones: the answers under the booking, side by side */
  @media (max-width: 1023px) {
    .card {
      flex-wrap: wrap;
      gap: 0.75rem;
    }

    .actions {
      width: 100%;
    }

    .actions button {
      flex: 1;
    }
  }
`;

/** The day still loading: the bookings' shape (time, name, service), pulsing */
const SkeletonList = styled.ul`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  padding: 0;
  list-style: none;

  li {
    display: flex;
    gap: 1rem;
    align-items: center;
    padding: 0.75rem 1rem 0.75rem 0.75rem;
    background: var(--color-neutral-dark);
    border-radius: 1rem;
  }

  .pill {
    flex: none;
    width: 3.25rem;
    height: 2rem;
    border-radius: 0.625rem;
  }

  .lines {
    display: flex;
    flex: 1;
    flex-direction: column;
    gap: 0.5rem;
  }

  .name {
    width: 40%;
    height: 0.875rem;
  }

  .meta {
    width: 60%;
    height: 0.75rem;
  }
`;
